import { reactive } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useSurveyStore } from "@stores/survey/survey";
import useMyFetch from "@stores/useMyFetch";
import { getClientId } from "@utils/clientId";
import { captureEvent } from "@/lib/analytics";
import { useChannel } from "./useChannel";

export type SummaryError = "no_verbatims" | "unavailable" | "timeout" | "failed";
export type SummaryStatus = "pending" | "streaming" | "done" | "failed";
export type SummaryStream = { text: string; status: SummaryStatus; error?: SummaryError };

type SummaryMessage = { question_id: number; filters?: string; text: string; status: Exclude<SummaryStatus, "pending">; error?: SummaryError };
type SavedSummary = { filters: string; summary: string };
type SummaryCard = { questions?: { id: number }[]; source_question_id?: number | null; verbatim_summaries?: SavedSummary[] };

const SILENCE_TIMEOUT_MS = 90_000;

const streams = reactive<Record<string, SummaryStream>>({});
const silenceTimers: Record<string, ReturnType<typeof setTimeout>> = {};
let subscribed = false;

export const isGenerating = (stream?: SummaryStream | null) => stream?.status === "pending" || stream?.status === "streaming";

export const summaryStreamKey = (questionId: number, filtersStr: string) => `${questionId}|${filtersStr}`;

function fail(key: string, error: SummaryError) {
  clearTimeout(silenceTimers[key]);
  streams[key] = { text: "", status: "failed", error };
}

function watchSilence(key: string) {
  clearTimeout(silenceTimers[key]);
  silenceTimers[key] = setTimeout(() => {
    if (isGenerating(streams[key])) fail(key, "timeout");
  }, SILENCE_TIMEOUT_MS);
}

function mergeSummary(questionId: number, filtersStr: string, summary: string) {
  (useResultsV2Store().availableCards as SummaryCard[]).forEach((card) => {
    const ownsQuestion = card.questions?.some((q) => q.id === questionId);
    if (!ownsQuestion && card.source_question_id !== questionId) return;
    card.verbatim_summaries = [
      ...(card.verbatim_summaries || []).filter((vs) => vs.filters !== filtersStr),
      { filters: filtersStr, summary },
    ];
  });
}

function handleReceivedMessage(msg: SummaryMessage) {
  if (msg.filters == null) return;
  const key = summaryStreamKey(msg.question_id, msg.filters);

  if (msg.status === "failed") return fail(key, msg.error || "failed");
  if (msg.status === "streaming") {
    streams[key] = { text: msg.text, status: "streaming" };
    return watchSilence(key);
  }

  clearTimeout(silenceTimers[key]);
  mergeSummary(msg.question_id, msg.filters, msg.text);
  delete streams[key];
}

export function useVerbatimSummariesStream() {
  const subscribe = () => {
    if (subscribed) return;
    subscribed = true;

    const { token: surveyToken } = storeToRefs(useSurveyStore());
    const channel = useChannel<SummaryMessage>("VerbatimSummariesChannel", {
      connected: () => channel.perform("subscribe", { survey_token: surveyToken.value }),
      received: handleReceivedMessage,
    });
    channel.subscribe({ survey_token: surveyToken.value });
  };

  const generateSummary = async (questionId: number, filters: Record<string, unknown>, target?: number | null) => {
    const key = summaryStreamKey(questionId, JSON.stringify(filters));
    streams[key] = { text: "", status: "pending" };

    try {
      const { response } = await useMyFetch(`/questions/${questionId}/generate_summary`)
        .post({ filters, target, client_id: getClientId() })
        .json<{ status: string }>();

      if (!response.value?.ok) return fail(key, "failed");
      captureEvent("verbatim_summary_generated", { question_id: questionId });
      watchSilence(key);
    } catch {
      fail(key, "failed");
    }
  };

  return { streams, subscribe, generateSummary };
}
